import { useState, useEffect, useRef, useCallback } from "react";

/**
 * useDeviceManager — Complete hardware device detection and classification.
 * 
 * Detects and categorizes:
 * - Capture cards (Elgato, Blackmagic, Magewell, AVerMedia, ATEM via USB)
 * - USB Webcams
 * - Audio interfaces (Focusrite, Behringer, MOTU, PreSonus, etc.)
 * - Built-in microphones, speakers
 * - HDMI/DisplayPort audio
 * 
 * Hot-plug support: auto-detects connect/disconnect in real-time.
 */

// Device classification patterns
const CAPTURE_CARD_PATTERNS = [
  { pattern: /elgato/i, brand: "Elgato", icon: "capture" },
  { pattern: /blackmagic|decklink|intensity|ultrastudio/i, brand: "Blackmagic", icon: "capture" },
  { pattern: /magewell/i, brand: "Magewell", icon: "capture" },
  { pattern: /avermedia|live\s*gamer/i, brand: "AVerMedia", icon: "capture" },
  { pattern: /cam\s*link/i, brand: "Elgato CamLink", icon: "capture" },
  { pattern: /atem/i, brand: "Blackmagic ATEM", icon: "capture" },
  { pattern: /hdmi.*capture|capture.*hdmi|usb.*capture|video.*capture/i, brand: "Carte de capture", icon: "capture" },
  { pattern: /thunderbolt/i, brand: "Thunderbolt", icon: "capture" },
];

const AUDIO_INTERFACE_PATTERNS = [
  { pattern: /focusrite|scarlett|clarett|saffire/i, brand: "Focusrite", icon: "audio_pro" },
  { pattern: /behringer|umc|uphoria|xenyx/i, brand: "Behringer", icon: "audio_pro" },
  { pattern: /motu/i, brand: "MOTU", icon: "audio_pro" },
  { pattern: /presonus|audiobox|studio/i, brand: "PreSonus", icon: "audio_pro" },
  { pattern: /universal\s*audio|apollo/i, brand: "Universal Audio", icon: "audio_pro" },
  { pattern: /steinberg|ur22|ur44/i, brand: "Steinberg", icon: "audio_pro" },
  { pattern: /audient/i, brand: "Audient", icon: "audio_pro" },
  { pattern: /native\s*instruments|komplete/i, brand: "Native Instruments", icon: "audio_pro" },
  { pattern: /rme|fireface|babyface/i, brand: "RME", icon: "audio_pro" },
  { pattern: /ssl|solid\s*state/i, brand: "SSL", icon: "audio_pro" },
  { pattern: /tascam/i, brand: "Tascam", icon: "audio_pro" },
  { pattern: /zoom\s*(h|u|f|r)/i, brand: "Zoom", icon: "audio_pro" },
  { pattern: /rode.*cast|rodecaster/i, brand: "Rode", icon: "audio_pro" },
  { pattern: /yamaha.*ag|ag0[36]/i, brand: "Yamaha", icon: "audio_pro" },
  { pattern: /mackie/i, brand: "Mackie", icon: "audio_pro" },
];

const PRO_MIC_PATTERNS = [
  { pattern: /rode/i, brand: "Rode", icon: "mic_pro" },
  { pattern: /shure/i, brand: "Shure", icon: "mic_pro" },
  { pattern: /blue\s*(yeti|snowball|spark)/i, brand: "Blue", icon: "mic_pro" },
  { pattern: /audio.technica|at2020|at2035/i, brand: "Audio-Technica", icon: "mic_pro" },
  { pattern: /sennheiser/i, brand: "Sennheiser", icon: "mic_pro" },
  { pattern: /hyperx/i, brand: "HyperX", icon: "mic_pro" },
  { pattern: /elgato.*wave/i, brand: "Elgato Wave", icon: "mic_pro" },
];

function classifyDevice(device) {
  const label = device.label || "";
  const kind = device.kind; // videoinput, audioinput, audiooutput

  const info = {
    id: device.deviceId,
    label: label || `Peripherique ${kind === "videoinput" ? "video" : kind === "audioinput" ? "audio (entree)" : "audio (sortie)"}`,
    kind,
    groupId: device.groupId,
    category: "unknown",
    brand: null,
    icon: "generic",
    isDefault: device.deviceId === "default",
    isCommunications: device.deviceId === "communications",
  };

  // Skip default/communications virtual devices for classification
  if (info.isDefault || info.isCommunications) {
    info.category = kind === "videoinput" ? "webcam" : kind === "audioinput" ? "mic_builtin" : "speaker_builtin";
    info.icon = kind === "videoinput" ? "webcam" : kind === "audioinput" ? "mic" : "speaker";
    return info;
  }

  if (kind === "videoinput") {
    // Check capture cards first
    for (const p of CAPTURE_CARD_PATTERNS) {
      if (p.pattern.test(label)) {
        info.category = "capture_card";
        info.brand = p.brand;
        info.icon = p.icon;
        return info;
      }
    }
    // Default: webcam
    info.category = "webcam";
    info.icon = "webcam";
    if (/logitech/i.test(label)) info.brand = "Logitech";
    else if (/microsoft/i.test(label)) info.brand = "Microsoft";
    else if (/razer/i.test(label)) info.brand = "Razer";
    return info;
  }

  if (kind === "audioinput") {
    // Check audio interfaces
    for (const p of AUDIO_INTERFACE_PATTERNS) {
      if (p.pattern.test(label)) {
        info.category = "audio_interface";
        info.brand = p.brand;
        info.icon = p.icon;
        return info;
      }
    }
    // Check pro mics
    for (const p of PRO_MIC_PATTERNS) {
      if (p.pattern.test(label)) {
        info.category = "mic_pro";
        info.brand = p.brand;
        info.icon = p.icon;
        return info;
      }
    }
    // HDMI/virtual audio
    if (/hdmi|displayport|monitor/i.test(label)) {
      info.category = "hdmi_audio";
      info.icon = "hdmi";
      return info;
    }
    // Built-in
    if (/built.in|internal|integrated|realtek|conexant/i.test(label)) {
      info.category = "mic_builtin";
      info.icon = "mic";
      return info;
    }
    info.category = "mic_other";
    info.icon = "mic";
    return info;
  }

  if (kind === "audiooutput") {
    // Check audio interfaces (output side)
    for (const p of AUDIO_INTERFACE_PATTERNS) {
      if (p.pattern.test(label)) {
        info.category = "audio_interface_out";
        info.brand = p.brand;
        info.icon = p.icon;
        return info;
      }
    }
    if (/hdmi|displayport|monitor/i.test(label)) {
      info.category = "hdmi_audio_out";
      info.icon = "hdmi";
      return info;
    }
    if (/built.in|internal|speaker|realtek/i.test(label)) {
      info.category = "speaker_builtin";
      info.icon = "speaker";
      return info;
    }
    info.category = "speaker_other";
    info.icon = "speaker";
    return info;
  }

  return info;
}

export default function useDeviceManager() {
  const [allDevices, setAllDevices] = useState([]);
  const [videoDevices, setVideoDevices] = useState([]);
  const [audioInputDevices, setAudioInputDevices] = useState([]);
  const [audioOutputDevices, setAudioOutputDevices] = useState([]);
  const [captureCards, setCaptureCards] = useState([]);
  const [audioInterfaces, setAudioInterfaces] = useState([]);
  const [permissionGranted, setPermissionGranted] = useState(false);
  const [lastEvent, setLastEvent] = useState(null); // { type: "connected"|"disconnected", device }
  const [scanning, setScanning] = useState(false);

  const prevDeviceIdsRef = useRef(new Set());

  const scan = useCallback(async (requestPermission = false) => {
    setScanning(true);
    try {
      if (requestPermission) {
        // Request both video + audio permission to get full device labels
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
          stream.getTracks().forEach(t => t.stop());
          setPermissionGranted(true);
        } catch {
          // Try video only
          try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: true });
            stream.getTracks().forEach(t => t.stop());
            setPermissionGranted(true);
          } catch {
            // Try audio only
            try {
              const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
              stream.getTracks().forEach(t => t.stop());
              setPermissionGranted(true);
            } catch {
              setPermissionGranted(false);
            }
          }
        }
      }

      const rawDevices = await navigator.mediaDevices.enumerateDevices();
      const classified = rawDevices
        .filter(d => d.deviceId && d.deviceId !== "")
        .map(classifyDevice);

      // Detect newly connected / disconnected devices
      const currentIds = new Set(classified.map(d => d.id));
      const prevIds = prevDeviceIdsRef.current;

      if (prevIds.size > 0) {
        // New device connected
        for (const d of classified) {
          if (!prevIds.has(d.id) && !d.isDefault && !d.isCommunications) {
            setLastEvent({ type: "connected", device: d, timestamp: Date.now() });
            break;
          }
        }
        // Device disconnected
        for (const prevId of prevIds) {
          if (!currentIds.has(prevId)) {
            setLastEvent({ type: "disconnected", deviceId: prevId, timestamp: Date.now() });
            break;
          }
        }
      }
      prevDeviceIdsRef.current = currentIds;

      setAllDevices(classified);
      setVideoDevices(classified.filter(d => d.kind === "videoinput"));
      setAudioInputDevices(classified.filter(d => d.kind === "audioinput"));
      setAudioOutputDevices(classified.filter(d => d.kind === "audiooutput"));
      setCaptureCards(classified.filter(d => d.category === "capture_card"));
      setAudioInterfaces(classified.filter(d =>
        d.category === "audio_interface" || d.category === "audio_interface_out"
      ));
    } catch {
      // enumerateDevices failed
    } finally {
      setScanning(false);
    }
  }, []);

  // Initial scan + hot-plug listener
  useEffect(() => {
    scan(false); // Silent scan first (may not have labels)

    const handler = () => scan(false);
    navigator.mediaDevices?.addEventListener?.("devicechange", handler);
    return () => navigator.mediaDevices?.removeEventListener?.("devicechange", handler);
  }, [scan]);

  // Clear last event after 5 seconds
  useEffect(() => {
    if (!lastEvent) return;
    const t = setTimeout(() => setLastEvent(null), 5000);
    return () => clearTimeout(t);
  }, [lastEvent]);

  return {
    allDevices,
    videoDevices,
    audioInputDevices,
    audioOutputDevices,
    captureCards,
    audioInterfaces,
    permissionGranted,
    lastEvent,
    scanning,
    scan,
  };
}
